// frontend/src/components/PlanBadge.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { getUsage } from "../api/orgs";
import { planMeta, TIER_TONES } from "../lib/planMeta";

/**
 * PlanBadge — a small, tier-coloured pill showing the org's current plan, with
 * an optional "Upgrade" affordance. Reads the live plan from
 * GET /api/orgs/me/usage and colours itself per tier (Free=grey, Starter=blue,
 * Pro=brand, Business=accent) via TIER_TONES (PRIVATE-AREA-PLAN PA1-T1/T2).
 *
 * Resilient by design: while loading or on error it renders nothing so it can
 * sit unconditionally in the topbar without ever breaking the shell.
 *
 * @param {boolean} [props.showUpgrade] Append an "Upgrade" link (hidden on the
 *   top tier). Used for the persistent topbar badge.
 * @param {object}  [props.usage] Pre-fetched usage payload; skips the fetch.
 */
function PlanBadge({ showUpgrade, usage }) {
  const [plan, setPlan] = useState(usage?.plan || null);

  useEffect(() => {
    if (usage?.plan) {
      setPlan(usage.plan);
      return undefined;
    }
    let active = true;
    getUsage()
      .then((d) => active && setPlan(d.plan))
      .catch(() => active && setPlan(null));
    return () => {
      active = false;
    };
  }, [usage]);

  if (!plan?.name) return null;

  const { id, isTop } = planMeta(plan.name);
  const tone = TIER_TONES[id] || TIER_TONES.free;

  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${tone}`}
        data-testid="plan-badge"
      >
        {plan.name}
      </span>
      {showUpgrade && !isTop && (
        <Link
          to="/billing"
          className="text-xs font-semibold text-brand-700 hover:text-brand-800"
        >
          Upgrade
        </Link>
      )}
    </span>
  );
}

PlanBadge.propTypes = {
  showUpgrade: PropTypes.bool,
  usage: PropTypes.object,
};
PlanBadge.defaultProps = {
  showUpgrade: false,
  usage: null,
};

export default PlanBadge;
